import { useEffect, useState } from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useCreateStaffTaskCommentMutation } from "services/staff-tasks";

type AddUserCommentProps = {
  taskId: number;
  isBusy?: boolean;
};

export default function AddUserComment({
  taskId,
  isBusy
}: AddUserCommentProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const [createComment, { isLoading }] = useCreateStaffTaskCommentMutation();

  const [value, setValue] = useState("");

  useEffect(() => {
    setValue("");
  }, [taskId]);

  const handleCreate = () => {
    createComment({ taskId, text: value, user: currentUser.id }).then(() => {
      setValue("");
    });
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      handleCreate();
    }
  };

  return (
    <>
      <Stack direction="row" width="100%" columnGap={2} alignItems="center">
        <Stack flex={2.5} justifyContent="flex-end" width="100%" mt={6}>
          <Box
            id="test"
            display="flex"
            sx={{
              mr: 2,
              mb: 2,
              width: "1px",
              background: grey[400],
              height: "30px",
              alignSelf: "flex-end"
            }}
          ></Box>
          <Stack direction="row" width="100%" columnGap={1}>
            <Stack display="flex" flex={5} justifyContent="center">
              <Typography
                variant="body1"
                sx={{
                  mt: 0.5,
                  textAlign: "right",
                  color: grey[700],
                  fontSize: "13px",
                  lineHeight: 1.2
                }}
              >
                {currentUser.fullName}
              </Typography>
            </Stack>
            <Box display="flex">
              <Avatar
                alt="avatar"
                src={currentUser.photo ?? ""}
                sx={{
                  border: `solid 1px ${grey[400]}`,
                  width: "32px",
                  height: "32px"
                }}
                slotProps={{ img: { loading: "lazy" } }}
              />
            </Box>
          </Stack>
        </Stack>
        <Stack flex={5} alignSelf="flex-end" mt={2}>
          <Stack direction="row">
            {isBusy || isLoading ? (
              <CircularProgress sx={{ py: 0 }} />
            ) : (
              <TextField
                value={value}
                placeholder="Enter your comment..."
                multiline
                maxRows={3}
                autoComplete="off"
                InputProps={{
                  readOnly: isLoading
                }}
                sx={{
                  width: "100%"
                }}
                onKeyDown={onKeyDown}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
              />
            )}

            <Box width="42px"></Box>
          </Stack>
          <Button
            sx={{ mt: 1, width: "65px" }}
            style={{ padding: "4px" }}
            size="small"
            variant="outlined"
            disabled={isLoading || isBusy}
            onClick={handleCreate}
          >
            Add
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
